import { useEffect, useRef, useState } from "react";
import type { SVGProps } from "react";
import { useMsal } from "@azure/msal-react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { RequireRole } from "../auth/RequireRole";
import { getEffectiveRole } from "../auth/roles";
import { useAccountRoles } from "../auth/useAccountRoles";
import { msalInstance } from "../auth/msalConfig";
import type { AdminOverview } from "../api/types";
import { CampaignCreatePanel } from "../features/campaigns/CampaignCreatePanel";
import { getOverview } from "../features/overview/api";
import { clearAllQueries, invalidatePrefix } from "../state/queryClient";
import { confirmActiveNavigation } from "../state/routeState";
import { useQuery } from "../state/useQuery";
import { MobileMoreSheet } from "./MobileMoreSheet";
import {
  findHiddenRouteTitle,
  findNavDestinationByPath,
  isMoreSheetDestination,
  MOBILE_BOTTOM_DESTINATIONS,
  NAV_GROUPS,
} from "./navigation";
import { RouteFocus } from "./RouteFocus";

/** Same 1.75px-stroke line-icon vocabulary as `navigation.tsx`. Decorative — callers mark it `aria-hidden`. */
function PlusIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={16}
      height={16}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      {...props}
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function MasIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={20}
      height={20}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M5 12h.01M12 12h.01M19 12h.01" />
    </svg>
  );
}

function LogoutIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={18}
      height={18}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3M10 17l5-5-5-5M15 12H4" />
    </svg>
  );
}

// Codex finding, panel-redesign-v3 PR #381: the Campañas badge's overview
// query never refreshed on its own — a long-lived session (a tab left open
// for hours) kept showing the priorities count from whenever it first
// mounted. `pollIntervalMs` makes `useQuery` repoll on its own cadence
// (visibility/online-aware, backs off on failure — see `useQuery.ts`);
// `OVERVIEW_FOCUS_REFETCH_MIN_GAP_MS` additionally lets a focus/visibility
// regain trigger an immediate refetch WITHOUT hammering the endpoint on
// rapid repeated focus events (e.g. quick alt-tabbing) by only firing once
// at least that long has passed since the last successful fetch.
export const OVERVIEW_POLL_INTERVAL_MS = 60_000;
export const OVERVIEW_FOCUS_REFETCH_MIN_GAP_MS = 30_000;

/** First letter of up to the first two words of `name` ("Carolina Góngora" → "CG"). Empty string when `name` is falsy. */
function getInitials(name: string | undefined): string {
  if (!name) {
    return "";
  }
  return name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? "")
    .join("");
}

/**
 * Shared shell (REDISENO-PANEL.md §3 "Shell y navegación", panel-redesign-v3
 * T2): skip link, product masthead, grouped navigation, "+ Nueva campaña"
 * (opens the existing `CampaignCreatePanel` from any screen), identity/
 * logout, one `<main id="main-content">` route outlet, and the mobile
 * top bar + bottom bar + "Más" sheet. Only two breakpoints exist now
 * (§3.1 desktop ≥768px, §3.2 mobile <768px) — the shell always renders the
 * SAME markup tree; `shell.css` media queries alone decide which half
 * (desktop rail vs. mobile top/bottom bars) is visible at a given width.
 */
export function AppShell() {
  const { accounts } = useMsal();
  const account = accounts[0];
  const roles = useAccountRoles();
  const location = useLocation();
  const activeDestination = findNavDestinationByPath(location.pathname);

  const [createCampaignOpen, setCreateCampaignOpen] = useState(false);
  const [moreSheetOpen, setMoreSheetOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  // Codex finding (round 4, PR #381): the desktop-only "+ Nueva campaña"
  // trigger (rendered only in the rail, hidden below 768px) can become
  // invisible by the time the create-campaign dialog closes if the operator
  // resized/rotated first — `Modal`'s default focus restoration would then
  // try to focus it and silently fall back to `<body>`. The mobile topbar's
  // own logo link is the one control guaranteed visible in that exact
  // opposite case (shown only below 768px), so it is this trigger's fallback
  // return target. Tracked via a ref (not state) so reading it inside
  // `nuevaCampanaRestoreFallback` always sees the CURRENT breakpoint at the
  // moment the dialog actually closes, not whatever it was when opened.
  const isDesktopViewportRef = useRef(
    typeof window.matchMedia === "function" ? window.matchMedia("(min-width: 768px)").matches : true,
  );
  const mobileTopbarLogoRef = useRef<HTMLAnchorElement>(null);
  // Codex finding (round 6, PR #381): always-visible desktop control used as
  // the mobile "Más" sheet's own focus-restoration fallback once the
  // viewport has crossed to desktop width (see `moreSheetRestoreFallback`
  // below) — the rail itself (and this brand link) is hidden below 768px,
  // the exact opposite breakpoint of the mobile topbar logo link above.
  const railBrandRef = useRef<HTMLAnchorElement>(null);

  // REDISENO-PANEL.md §3.1 "Badge rojo en 'Campañas' con el número de
  // overview.priorities.length cuando sea > 0" — reuses the SAME overview
  // query/cache key OverviewScreen.tsx already populates (state/queryClient.ts
  // dedupes same-key fetches across mounted instances), no new endpoint.
  const {
    data: overview,
    loading: overviewLoading,
    refreshing: overviewRefreshing,
    refetch: refetchOverview,
  } = useQuery<AdminOverview>({
    key: ["overview"],
    queryFn: getOverview,
    pollIntervalMs: OVERVIEW_POLL_INTERVAL_MS,
  });
  const priorityCount = overview?.priorities.length ?? 0;

  // Codex finding (panel-redesign-v3 PR #381): on top of the background poll
  // above, refetch immediately when the tab regains focus or becomes visible
  // again — the common "left it open, switched away, came back" shape — but
  // only when enough time has actually passed since the last fetch, so rapid
  // repeated focus events (e.g. quick alt-tabbing) never hammer the endpoint.
  // Read through a ref (not effect deps) so the listeners are registered
  // once and always see the latest fetch/refetch without re-binding on every
  // poll.
  //
  // Review fix (panel-redesign-v3-02): the gate used to key off `updatedAt`
  // (last SUCCESS). During an outage — the request never succeeds, or keeps
  // failing — `updatedAt` never advances, so every focus/visibilitychange
  // event refetched with no rate limit at all. `lastAttemptAt` instead
  // records the moment a fetch (of ANY origin — the initial mount fetch, a
  // background poll tick, or a focus-triggered refetch) was last ATTEMPTED,
  // whether it goes on to succeed or fail, and `fetching` additionally skips
  // firing a second request while one is still pending — together these also
  // collapse a `focus` + `visibilitychange` pair that both fire
  // synchronously for the same real-world event into a single refetch.
  const overviewRefreshRef = useRef({
    refetch: refetchOverview,
    lastAttemptAt: undefined as number | undefined,
    fetching: false,
  });
  overviewRefreshRef.current.refetch = refetchOverview;

  // Mirrors `loading`/`refreshing` (true for ANY in-flight fetch, regardless
  // of what triggered it) onto the ref above, stamping `lastAttemptAt` the
  // moment a fetch transitions from idle to in-flight.
  const wasFetchingRef = useRef(false);
  useEffect(() => {
    const fetchingNow = overviewLoading || overviewRefreshing;
    if (fetchingNow && !wasFetchingRef.current) {
      overviewRefreshRef.current.lastAttemptAt = Date.now();
    }
    overviewRefreshRef.current.fetching = fetchingNow;
    wasFetchingRef.current = fetchingNow;
  }, [overviewLoading, overviewRefreshing]);

  useEffect(() => {
    const maybeRefetchOverview = () => {
      if (document.hidden) {
        return;
      }
      const state = overviewRefreshRef.current;
      if (state.fetching) {
        return;
      }
      const now = Date.now();
      if (state.lastAttemptAt !== undefined && now - state.lastAttemptAt < OVERVIEW_FOCUS_REFETCH_MIN_GAP_MS) {
        return;
      }
      // Set synchronously (not via the effect above, which only runs after
      // the next render/commit) so a `focus` + `visibilitychange` pair
      // firing back-to-back in the same tick sees `fetching: true` on the
      // second call and never double-refetches.
      state.lastAttemptAt = now;
      state.fetching = true;
      state.refetch();
    };
    window.addEventListener("focus", maybeRefetchOverview);
    document.addEventListener("visibilitychange", maybeRefetchOverview);
    return () => {
      window.removeEventListener("focus", maybeRefetchOverview);
      document.removeEventListener("visibilitychange", maybeRefetchOverview);
    };
  }, []);

  // Close the "Más" sheet whenever the route actually changes (e.g. browser
  // back/forward) — clicking a sheet link already closes it via
  // `handleSheetNavigate` below, this is just a safety net.
  useEffect(() => {
    setMoreSheetOpen(false);
  }, [location.pathname]);

  // Codex finding (panel-redesign-v3 PR #381): `createCampaignOpen` is shell-
  // level state rendered outside the route `<Outlet>` (same as
  // `moreSheetOpen` above) — navigating to a different screen while the
  // create-campaign dialog was open left it mounted and open on top of the
  // new screen's content instead of closing with the navigation that caused it.
  useEffect(() => {
    setCreateCampaignOpen(false);
  }, [location.pathname]);

  // Codex finding (panel-redesign-v3 PR #381): the mobile "Más" sheet stayed
  // open if it was opened on a narrow viewport and the window was then
  // resized/rotated past the desktop breakpoint (768px, same value as
  // shell.css's two breakpoints) without a route change — the mobile bottom
  // bar it belongs to is hidden at that width, leaving an orphaned open
  // dialog. Close it the moment the viewport crosses into desktop.
  useEffect(() => {
    if (typeof window.matchMedia !== "function") {
      return undefined;
    }
    const desktopQuery = window.matchMedia("(min-width: 768px)");
    isDesktopViewportRef.current = desktopQuery.matches;
    const handleChange = (event: MediaQueryListEvent) => {
      // Codex finding (round 4, PR #381): kept in sync on EVERY crossing
      // (not just into desktop) so `nuevaCampanaRestoreFallback` always
      // reads the breakpoint that is actually current when the
      // create-campaign dialog closes.
      isDesktopViewportRef.current = event.matches;
      if (event.matches) {
        setMoreSheetOpen(false);
      }
    };
    // Safari <14 (and other older WebKit) never implemented the modern
    // `MediaQueryList` EventTarget methods, only the legacy
    // `addListener`/`removeListener` pair — without this fallback the
    // breakpoint-crossing close silently never fired there.
    if (typeof desktopQuery.addEventListener === "function") {
      desktopQuery.addEventListener("change", handleChange);
      return () => {
        desktopQuery.removeEventListener("change", handleChange);
      };
    }
    desktopQuery.addListener(handleChange);
    return () => {
      desktopQuery.removeListener(handleChange);
    };
  }, []);

  /**
   * #194: a nav-link click is an in-app navigation like any other — an
   * editor screen's unsaved changes must block it exactly like Back/
   * beforeunload already do. `confirmActiveNavigation()` is a no-op (true,
   * no prompt) whenever nothing is dirty, so this never adds friction to
   * the common case.
   */
  const guardNavClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!confirmActiveNavigation()) {
      event.preventDefault();
    }
  };

  const handleSheetNavigate = (event: React.MouseEvent<HTMLAnchorElement>) => {
    guardNavClick(event);
    if (!event.defaultPrevented) {
      setMoreSheetOpen(false);
    }
  };

  const handleLogout = () => {
    clearAllQueries();
    void msalInstance.logoutRedirect();
  };

  const openCreateCampaign = () => setCreateCampaignOpen(true);
  const closeCreateCampaign = () => setCreateCampaignOpen(false);
  // Codex finding (round 4, PR #381): called lazily by `Modal` at CLOSE
  // time — `null` keeps the normal restoration (desktop trigger still
  // visible); the mobile topbar logo link only once the viewport has
  // actually crossed to mobile, where that trigger is hidden.
  const nuevaCampanaRestoreFallback = () => (isDesktopViewportRef.current ? null : mobileTopbarLogoRef.current);
  // Codex finding (round 6, PR #381): the reverse case of the fallback
  // above — the mobile "Más" sheet's own trigger lives in the mobile bottom
  // bar, hidden by the SAME desktop media query once the viewport crosses
  // past 768px. This effect already closes the sheet on that exact
  // crossing (below), so `MobileMoreSheet`'s default restoration would try
  // to focus that now-hidden trigger; fall back to the always-visible
  // desktop rail brand link instead.
  const moreSheetRestoreFallback = () => (isDesktopViewportRef.current ? railBrandRef.current : null);
  const handleCampaignCreated = () => {
    setCreateCampaignOpen(false);
    // No new endpoint: invalidating these two already-fetched query keys
    // makes any mounted screen (CampaignListScreen, OverviewScreen, this
    // shell's own badge) refetch on its own, the same mechanism
    // CampaignListScreen's local "Nueva campaña" trigger already used
    // (`refetch()`) — here there may be no CampaignListScreen mounted at
    // all (the rail is reachable from any screen), so the cache-level
    // invalidation is the one mechanism that reaches every possible
    // subscriber.
    invalidatePrefix(["campaigns"]);
    invalidatePrefix(["overview"]);
  };

  const topbarSubtitle = activeDestination
    ? (activeDestination.topbarLabel ?? activeDestination.label)
    : (findHiddenRouteTitle(location.pathname) ?? "Página no encontrada");
  // `moreSheetActive` also highlights the bottom-bar "Más" button while the
  // sheet is merely open (visual affordance, regardless of destination);
  // `aria-current` must stay keyed ONLY to the actual active section
  // (`isMoreSheetDestination`) — opening the sheet from an unrelated
  // destination like Inicio must not also claim it as the "Más" section.
  const moreSheetActive = moreSheetOpen || isMoreSheetDestination(location.pathname);
  const moreSheetIsActiveSection = isMoreSheetDestination(location.pathname);
  const initials = getInitials(account?.name ?? account?.username);

  return (
    <div className="panel-shell-v2 v2-shell">
      <a className="v2-shell__skip-link v2-u-target-min" href="#main-content">
        Saltar al contenido principal
      </a>

      <header className="v2-shell__topbar">
        <div className="v2-shell__topbar-titles">
          <Link
            to="/"
            ref={mobileTopbarLogoRef}
            className="v2-shell__topbar-logo-plate"
            onClick={guardNavClick}
          >
            <img
              className="v2-shell__topbar-logo"
              src="/assets/logo-control-logistico.png"
              alt="Control Logístico"
            />
          </Link>
          <span className="v2-shell__topbar-subtitle">{topbarSubtitle}</span>
        </div>
      </header>

      <nav id="v2-shell-nav" ref={navRef} aria-label="Navegación principal" className="v2-shell__nav">
        <Link to="/" ref={railBrandRef} className="v2-shell__rail-brand" onClick={guardNavClick}>
          <span className="v2-shell__rail-brand-logo-plate">
            <img
              className="v2-shell__rail-brand-logo"
              src="/assets/logo-control-logistico.png"
              alt="Control Logístico"
            />
          </span>
          <span className="v2-shell__rail-brand-sub">Email Marketing B2B</span>
        </Link>

        {/* REDISENO-PANEL.md §3.1: secondary action (never the single
            primary-blue button per screen) — opens the SAME CampaignCreatePanel
            CampaignListScreen already mounts locally, available from any
            screen now that it is rendered at the shell level. */}
        <RequireRole role="Operador">
          <button
            type="button"
            className="v2-shell__nueva-campana v2-u-target-min"
            onClick={openCreateCampaign}
          >
            <PlusIcon aria-hidden="true" />
            Nueva campaña
          </button>
        </RequireRole>

        {NAV_GROUPS.filter((group) => group.destinations.length > 0).map((group) => (
          <div className="v2-shell__nav-group" key={group.key}>
            <span className="v2-shell__nav-group-label">{group.label}</span>
            {group.destinations.map((destination) => (
              <NavLink
                key={destination.key}
                to={destination.to}
                end={destination.to === "/"}
                title={destination.label}
                className={({ isActive }) =>
                  `v2-shell__nav-link v2-u-target-min${isActive ? " v2-shell__nav-link--active" : ""}`
                }
                onClick={guardNavClick}
              >
                <destination.Icon aria-hidden="true" className="v2-shell__nav-icon" />
                <span className="v2-shell__nav-link-label">{destination.label}</span>
                {destination.key === "campanas" && priorityCount > 0 && (
                  // Codex finding (panel-redesign-v3 PR #381): a bare number
                  // ("Campañas 3") gives assistive technology no indication
                  // this is a count of pending priorities/warnings.
                  // `aria-label` carries the semantic text for the
                  // accessible name while the visible digit stays compact.
                  <span className="v2-shell__nav-badge" aria-label={`${priorityCount} prioridades`}>
                    {priorityCount}
                  </span>
                )}
              </NavLink>
            ))}
          </div>
        ))}

        <UserMenu account={account} roles={roles} initials={initials} onLogout={handleLogout} />
      </nav>

      <main id="main-content" className="v2-shell__main">
        <RouteFocus />
        <div className="v2-shell__content">
          <Outlet />
        </div>
      </main>

      <div aria-live="polite" className="v2-shell__announce" />

      <nav aria-label="Navegación móvil" className="v2-shell__bottom-bar">
        {MOBILE_BOTTOM_DESTINATIONS.map((destination) => {
          // Codex finding (panel-redesign-v3 PR #381): `NavLink`'s own
          // `isActive` (what the default non-`end` prefix match produces)
          // still matched an unregistered catch-all depth like
          // `/contactos/id/extra` — the same path `findNavDestinationByPath`
          // already rejects as the real 404 above. A plain `<Link>` with the
          // visual `--active` class and `aria-current` driven off that SAME
          // registered-depth predicate (instead of `NavLink`) means this
          // bottom-bar link can never claim "current" at the same time the
          // shell is already showing the 404 and marking "Más" current.
          // (Passing `aria-current={undefined}` to `NavLink` does not work
          // for this: its destructuring default re-substitutes "page" for an
          // explicit `undefined`, so it cannot be used to suppress the
          // attribute — only `<Link>` with the attribute omitted outright can.)
          const isRegisteredActive = activeDestination?.key === destination.key;
          return (
            <Link
              key={destination.key}
              to={destination.to}
              aria-current={isRegisteredActive ? "page" : undefined}
              className={`v2-shell__bottom-link v2-u-target-min${isRegisteredActive && !moreSheetOpen ? " v2-shell__bottom-link--active" : ""}`}
              onClick={guardNavClick}
            >
              <destination.Icon aria-hidden="true" className="v2-shell__bottom-icon" />
              <span className="v2-shell__bottom-link-label">{destination.label}</span>
            </Link>
          );
        })}
        <button
          type="button"
          className={`v2-shell__bottom-link v2-u-target-min${moreSheetActive ? " v2-shell__bottom-link--active" : ""}`}
          aria-haspopup="dialog"
          aria-expanded={moreSheetOpen}
          aria-current={moreSheetIsActiveSection ? "page" : undefined}
          onClick={() => setMoreSheetOpen((open) => !open)}
        >
          <MasIcon aria-hidden="true" className="v2-shell__bottom-icon" />
          <span className="v2-shell__bottom-link-label">Más</span>
        </button>
      </nav>

      <MobileMoreSheet
        open={moreSheetOpen}
        onClose={() => setMoreSheetOpen(false)}
        onNavigate={handleSheetNavigate}
        onLogout={handleLogout}
        accountName={account?.name ?? account?.username ?? ""}
        roleLabel={getEffectiveRole(roles)}
        initials={initials}
        restoreFocusFallback={moreSheetRestoreFallback}
      />

      {createCampaignOpen && (
        <CampaignCreatePanel
          onClose={closeCreateCampaign}
          onCreated={handleCampaignCreated}
          restoreFocusFallback={nuevaCampanaRestoreFallback}
        />
      )}
    </div>
  );
}

/**
 * Rail footer identity block (REDISENO-PANEL.md §3.1: "avatar con iniciales,
 * nombre de la cuenta, rol ... y un ícono de cerrar sesión") — an icon-only
 * logout action (not the previous text button), same accessible name
 * ("Cerrar sesión") so it stays discoverable the same way.
 */
function UserMenu({
  account,
  roles,
  initials,
  onLogout,
}: {
  account: { name?: string; username?: string } | undefined;
  roles: readonly string[];
  initials: string;
  onLogout: () => void;
}) {
  if (!account) {
    return null;
  }

  return (
    <div className="v2-shell__user-menu">
      <span className="v2-shell__user-avatar" aria-hidden="true">
        {initials}
      </span>
      <span className="v2-shell__user-identity">
        <span className="v2-shell__user-name">{account.name ?? account.username}</span>
        {roles.length > 0 && <span className="v2-shell__user-role">{roles.join(", ")}</span>}
      </span>
      <button
        type="button"
        className="v2-shell__user-logout v2-u-target-min"
        aria-label="Cerrar sesión"
        title="Cerrar sesión"
        onClick={onLogout}
      >
        <LogoutIcon aria-hidden="true" />
      </button>
    </div>
  );
}
